/**
 * The browser half of Web Push: permission, subscription, and keeping the
 * server's copy of that subscription true.
 *
 * Three things about this are worth knowing before changing anything here.
 *
 * **iOS only works from the Home Screen.** Safari refuses
 * `pushManager.subscribe()` in a normal tab; the app has to be installed
 * (Share → Add to Home Screen, iOS 16.4+). {@link pushSupport} reports that
 * case separately from "not supported" so the settings screen can say what to
 * do instead of a dead toggle.
 *
 * **Permission must come from a click.** Browsers ignore — and Safari
 * penalises — a permission prompt raised on page load, so `enablePush` is only
 * ever called from a button.
 *
 * **The subscription is not permanent.** A browser can rotate or drop it on
 * its own, and the service worker cannot tell the server (it holds no access
 * token). {@link syncPushSubscription} runs at app start and re-registers
 * whatever the browser currently has, which heals a rotated endpoint and is
 * cheap — the server upserts on the endpoint.
 */
import { registerPushSubscription, removePushSubscription } from './pushApi';
import { isDesktop } from './desktop';

/** Why the push toggle can't be offered, when it can't. */
export type PushSupport =
  /** Everything needed is present. */
  | 'supported'
  /** iOS Safari in a tab: works, but only once installed to the Home Screen. */
  | 'needs-install'
  /** No service worker or no Push API — an old or stripped-down browser. */
  | 'unsupported'
  /**
   * The Firmio desktop app: Electron has no Web Push, and needs none — it
   * shows system notifications from the notification feed instead
   * (hooks/useDesktopNotifications.ts).
   */
  | 'desktop';

function isStandalone(): boolean {
  if (typeof window === 'undefined') return false;
  return (
    window.matchMedia?.('(display-mode: standalone)').matches ||
    // iOS Safari's own flag; it predates display-mode and is still the only
    // reliable signal there.
    (window.navigator as Navigator & { standalone?: boolean }).standalone === true
  );
}

function isIos(): boolean {
  if (typeof navigator === 'undefined') return false;
  // iPadOS 13+ reports itself as a Mac; the touch-point count is what separates
  // an iPad from a desktop Safari.
  return (
    /iPad|iPhone|iPod/.test(navigator.userAgent) ||
    (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1)
  );
}

export function pushSupport(): PushSupport {
  if (typeof window === 'undefined' || typeof navigator === 'undefined') return 'unsupported';
  if (isDesktop()) return 'desktop';
  const hasApis =
    'serviceWorker' in navigator && 'PushManager' in window && 'Notification' in window;
  if (!hasApis) {
    // An iPhone on a supported iOS version hides these APIs entirely until the
    // app is installed, so "missing APIs" there means "install it", not "your
    // browser can't".
    return isIos() && !isStandalone() ? 'needs-install' : 'unsupported';
  }
  if (isIos() && !isStandalone()) return 'needs-install';
  return 'supported';
}

/** Current browser permission, without prompting. */
export function pushPermission(): NotificationPermission | 'unsupported' {
  if (typeof Notification === 'undefined') return 'unsupported';
  return Notification.permission;
}

/**
 * VAPID keys travel as base64url text; `pushManager.subscribe` wants the raw
 * bytes. Buried here because it is the one piece of Web Push that is pure
 * plumbing and easy to get subtly wrong.
 */
function urlBase64ToUint8Array(base64: string): Uint8Array<ArrayBuffer> {
  const padded = base64.padEnd(base64.length + ((4 - (base64.length % 4)) % 4), '=');
  const raw = window.atob(padded.replace(/-/g, '+').replace(/_/g, '/'));
  // Backed by an explicit ArrayBuffer: `new Uint8Array(length)` widens to
  // ArrayBufferLike, which `applicationServerKey` (a BufferSource) rejects
  // because that would admit a SharedArrayBuffer.
  const bytes = new Uint8Array(new ArrayBuffer(raw.length));
  for (let i = 0; i < raw.length; i += 1) bytes[i] = raw.charCodeAt(i);
  return bytes;
}

/**
 * The active service-worker registration, or null.
 *
 * `navigator.serviceWorker.ready` never settles when nothing is registered —
 * it waits forever rather than rejecting. That is every dev session (the app
 * deliberately skips registration outside production, see lib/pwa.ts) and any
 * production load where registration failed, and an un-settling promise here
 * leaves the settings panel stuck on "loading" with no way out. So the wait is
 * bounded, and a timeout reads as "no service worker".
 */
async function readyRegistration(): Promise<ServiceWorkerRegistration | null> {
  if (!('serviceWorker' in navigator)) return null;
  try {
    const timeout = new Promise<null>((resolve) => setTimeout(() => resolve(null), 3000));
    return await Promise.race([navigator.serviceWorker.ready, timeout]);
  } catch {
    return null;
  }
}

/** The subscription this browser currently holds, if any. */
export async function currentSubscription(): Promise<PushSubscription | null> {
  const registration = await readyRegistration();
  if (!registration) return null;
  try {
    return await registration.pushManager.getSubscription();
  } catch {
    return null;
  }
}

export class PushError extends Error {
  constructor(
    message: string,
    /** 'denied' when the person said no — the one case the UI must explain. */
    public readonly reason: 'denied' | 'unsupported' | 'failed',
  ) {
    super(message);
    this.name = 'PushError';
  }
}

/**
 * Ask for permission, subscribe, and register the subscription with the server.
 * Must be called from a user gesture.
 */
export async function enablePush(publicKey: string): Promise<void> {
  if (pushSupport() !== 'supported') {
    throw new PushError('Browserul nu acceptă notificări push aici.', 'unsupported');
  }

  const permission = await Notification.requestPermission();
  if (permission !== 'granted') {
    throw new PushError('Permisiunea pentru notificări a fost refuzată.', 'denied');
  }

  const registration = await readyRegistration();
  if (!registration) {
    throw new PushError('Service worker-ul nu este activ.', 'unsupported');
  }

  // Reuse an existing subscription when there is one: subscribing twice with
  // the same key returns the same endpoint anyway, and asking again is slower.
  const existing = await registration.pushManager.getSubscription();
  const subscription =
    existing ??
    (await registration.pushManager.subscribe({
      // Required, and honest: every push we send shows a notification.
      userVisibleOnly: true,
      applicationServerKey: urlBase64ToUint8Array(publicKey),
    }));

  await registerPushSubscription(subscription.toJSON());
}

/**
 * Stop notifications in this browser: drop the subscription with the vendor and
 * the row on the server.
 *
 * The vendor side goes first. If the server call fails afterwards the row is
 * orphaned, and the next send deletes it on the vendor's 410 — whereas the
 * other order can leave a browser still receiving pushes we no longer track.
 */
export async function disablePush(): Promise<void> {
  const subscription = await currentSubscription();
  if (!subscription) return;
  const { endpoint } = subscription;
  try {
    await subscription.unsubscribe();
  } catch {
    /* Already gone on the vendor side; still drop our row. */
  }
  await removePushSubscription(endpoint);
}

/**
 * Re-register whatever subscription this browser currently holds.
 *
 * Runs once at app start. It is how a rotated endpoint gets back in sync
 * without an unauthenticated endpoint for the service worker to post to, and
 * how a person who cleared site data on one device stops having a dead row.
 * Silent by design: nothing here is worth a message on the screen.
 */
export async function syncPushSubscription(): Promise<void> {
  try {
    if (pushSupport() !== 'supported' || pushPermission() !== 'granted') return;
    const subscription = await currentSubscription();
    if (!subscription) return;
    await registerPushSubscription(subscription.toJSON());
  } catch {
    /* Offline, logged out, or push disabled server-side. Try again next start. */
  }
}
